import React, { type ChangeEvent, forwardRef, type ReactNode, useContext } from 'react';

import classnames from 'classnames';

import { FormControlMessage, Icon, Text, Tooltip, type TooltipProps } from 'styled-system';

import { TOOLTIP } from '../../../dataTestIds';
import useControlledState from '../../hooks/useControlledState';
import { type InputBaseProps } from '../../types/formControls';
import { devWarn } from '../../utils';

import RadioGroupContext from './context';

import styles from './Radio.module.scss';

export type RadioProps = InputBaseProps<{
    /**
     * The content of the checkbox label.
     */
    children?: ReactNode;
    /**
     * If the checkbox is disabled.
     */
    disabled?: boolean;
    /**
     * Whether the checkbox is checked.
     */
    checked?: boolean;
    /**
     * The additional css class.
     */
    className?: string;
    /**
     * Specifies the initial state: whether the checkbox is selected.
     */
    defaultChecked?: boolean;
    /**
     * An informatory tooltip that is displayed when hovering over the label icon.
     */
    tooltip?: string;
    tooltipId?: string;
    tooltipPlacement?: TooltipProps['placement'];
    /** String to show a help message below the field. */
    helperText?: string;
}>;

export type RadioChangeEvent = ChangeEvent<HTMLInputElement>;

export const Radio = forwardRef<HTMLInputElement, RadioProps>((props: RadioProps, ref) => {
    const groupContext = useContext(RadioGroupContext);
    const {
        children,
        className,
        defaultChecked,
        disabled,
        tooltip,
        tooltipPlacement = 'top',
        tooltipId = '',
        helperText,
        onChange = groupContext?.onChange,
        ...rest
    } = props;

    const controlledValue = groupContext
        ? groupContext.value?.toString() === props.value?.toString()
        : props.checked;

    const [isChecked, setIsChecked] = useControlledState(
        controlledValue,
        defaultChecked,
        'Radio',
        'checked'
    );

    const radioProps = groupContext
        ? {
              ...rest,
              name: groupContext.name,
              disabled: Boolean(groupContext.disabled) || Boolean(disabled),
              checked: isChecked
          }
        : {
              ...rest,
              disabled: Boolean(disabled),
              checked: isChecked
          };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (radioProps.disabled) {
            return;
        }
        setIsChecked(e.target.checked);
        onChange?.(e);
    };

    if (process.env.NODE_ENV !== 'production') {
        if (!children && !radioProps?.['aria-label']) {
            devWarn('Provide an aria-label for accessibility.');
        }
    }
    const LabelElement = children ? 'label' : 'div';

    return (
        <div className={classnames(styles.container, className)}>
            <LabelElement
                className={classnames(styles.labelContainer, {
                    [styles.disabled]: radioProps.disabled,
                    [styles.checked]: radioProps.checked
                })}
            >
                <span className={styles.wrapper}>
                    <input
                        ref={ref}
                        type="radio"
                        aria-checked={!!radioProps.checked}
                        onChange={handleChange}
                        {...radioProps}
                    />
                    <span className={styles.inner}>
                        <span className={styles.circle} />
                    </span>
                </span>
                {children && <Text className={styles.label}>{children}</Text>}
                {tooltip && (
                    <Tooltip
                        id={tooltipId}
                        trigger={['hover', 'focus']}
                        placement={tooltipPlacement}
                        title={tooltip}
                    >
                        <Icon
                            data-testid={TOOLTIP(tooltipId)}
                            className={styles.icon}
                            name="s-icon-circle-info"
                            tabIndex={0}
                        />
                    </Tooltip>
                )}
            </LabelElement>
            {helperText && (
                <FormControlMessage
                    className={styles.helpText}
                    message={helperText}
                    type="helper"
                />
            )}
        </div>
    );
});

Radio.displayName = 'Radio';
